import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Badge } from "../app/components/primitives/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../app/components/primitives/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Plus, Trash2 } from "lucide-react";

type ExclusionKind = API.EmailCaptureExclusion["kind"];

export function SettingsEmailCaptureExclusionsPage() {
  const dataAdapter = useDataAdapter();
  const [exclusions, setExclusions] = useState<API.EmailCaptureExclusion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [newPattern, setNewPattern] = useState("");
  const [newKind, setNewKind] = useState<ExclusionKind>("domain");
  const [newReason, setNewReason] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadExclusions = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await dataAdapter.listEmailCaptureExclusions();
      setExclusions(result);
    } catch {
      setLoadError("Couldn't load email capture exclusions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExclusions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetCreateForm = () => {
    setIsCreating(false);
    setNewPattern("");
    setNewKind("domain");
    setNewReason("");
    setCreateError(null);
  };

  const handleCreate = async () => {
    const pattern = newPattern.trim();
    const reason = newReason.trim();
    if (!pattern) return;
    setIsSaving(true);
    setCreateError(null);
    try {
      await dataAdapter.addEmailCaptureExclusion({
        pattern,
        kind: newKind,
        reason,
      });
      resetCreateForm();
      await loadExclusions();
    } catch (err) {
      setCreateError(
        err instanceof ApiError
          ? err.detail || "Couldn't add that exclusion."
          : "Couldn't add that exclusion.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeId) return;
    await dataAdapter.removeEmailCaptureExclusion(removeId);
    setRemoveId(null);
    await loadExclusions();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-2">Email Capture Exclusions</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Senders and domains excluded from customer email capture — HR,
            recruitment, and notification noise that shouldn't land on a
            customer record. Matches the message sender or any recipient in To,
            never a CC. Adding an entry only affects emails captured{" "}
            <span className="font-medium">from now on</span> — it does not
            remove mail already captured.
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="size-4 mr-2" />
          New Exclusion
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pattern</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="w-16">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isCreating && (
              <TableRow>
                <TableCell>
                  <Input
                    autoFocus
                    value={newPattern}
                    onChange={(e) => setNewPattern(e.target.value)}
                    placeholder="linkedin.com or noreply@github.com"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") resetCreateForm();
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={newKind}
                    onValueChange={(v) => setNewKind(v as ExclusionKind)}
                  >
                    <SelectTrigger aria-label="Kind" className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domain">Domain</SelectItem>
                      <SelectItem value="address">Address</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell colSpan={2}>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Input
                        value={newReason}
                        onChange={(e) => setNewReason(e.target.value)}
                        placeholder="Why does this entry exist? (optional)"
                        aria-label="Reason (optional)"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleCreate();
                          if (e.key === "Escape") resetCreateForm();
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={handleCreate}
                        disabled={!newPattern.trim() || isSaving}
                      >
                        Add
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={resetCreateForm}
                      >
                        Cancel
                      </Button>
                    </div>
                    {createError && (
                      <p className="text-sm text-destructive">{createError}</p>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-8 text-destructive"
                >
                  {loadError}
                </TableCell>
              </TableRow>
            ) : exclusions.length === 0 && !isCreating ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-8 text-muted-foreground"
                >
                  No exclusions yet
                </TableCell>
              </TableRow>
            ) : (
              exclusions.map((exclusion) => (
                <TableRow key={exclusion.id}>
                  <TableCell className="font-medium">
                    {exclusion.pattern}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {exclusion.kind === "domain" ? "Domain" : "Address"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {exclusion.reason || (
                      <span className="italic">No reason given</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${exclusion.pattern}`}
                      onClick={() => setRemoveId(exclusion.id)}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!removeId} onOpenChange={() => setRemoveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this exclusion?</AlertDialogTitle>
            <AlertDialogDescription>
              Future emails from this sender or domain will be captured again.
              This does not restore any mail that was excluded while this entry
              existed — there is nothing to restore, since it was never
              captured.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
